/**
 * Moteur du simulateur de post LinkedIn : pur, sans React.
 *
 * Seule limite officielle : 3 000 caractères par post (aide LinkedIn,
 * « Post and share updates »). LinkedIn ne publie ni la position de la coupure
 * « voir plus » ni un nombre de hashtags recommandé. La coupure est donc
 * ESTIMÉE par un repli de lignes (largeur en caractères × nombre de lignes
 * visibles) et affichée comme telle ; les autres repères (paragraphe trop
 * long, nombre de hashtags) sont des repères éditoriaux NeverZero, pas des
 * règles de la plateforme. Aucune prédiction de portée.
 */

export const LINKEDIN_MAX_CHARS = 3000;

export type Device = 'mobile' | 'desktop';

export interface DeviceModel {
  /** Caractères par ligne, estimation moyenne en police proportionnelle. */
  width: number;
  /** Lignes visibles avant « …voir plus ». */
  lines: number;
}

export const DEVICES: Record<Device, DeviceModel> = {
  mobile: { width: 42, lines: 3 },
  desktop: { width: 88, lines: 5 },
};

/** Place réservée au « …voir plus » sur la dernière ligne visible. */
const SEE_MORE_RESERVE = 10;

export interface Cut {
  /** Texte visible avant la coupure (vide si le post tient en entier). */
  visible: string;
  /** Reste masqué derrière « voir plus ». */
  hidden: string;
  /** Faux si le post tient en entier : aucun « voir plus » n'apparaît. */
  truncated: boolean;
}

/** Replie un texte en lignes d'affichage, en respectant retours à la ligne et mots. */
export function wrapLines(text: string, width: number): string[] {
  const out: string[] = [];
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '') {
      out.push('');
      continue;
    }
    let line = '';
    for (const word of raw.split(/\s+/).filter(Boolean)) {
      let w = word;
      while (w.length > width) {
        if (line) {
          out.push(line);
          line = '';
        }
        out.push(w.slice(0, width));
        w = w.slice(width);
      }
      if (!line) line = w;
      else if (line.length + 1 + w.length <= width) line += ` ${w}`;
      else {
        out.push(line);
        line = w;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

export function computeCut(text: string, device: Device): Cut {
  const { width, lines } = DEVICES[device];
  const wrapped = wrapLines(text.trim(), width);
  if (wrapped.length <= lines) return { visible: text.trim(), hidden: '', truncated: false };

  const kept = wrapped.slice(0, lines);
  // La dernière ligne visible cède de la place à « …voir plus ».
  let last = kept[lines - 1];
  const limit = width - SEE_MORE_RESERVE;
  if (last.length > limit) {
    const cutAt = last.lastIndexOf(' ', limit);
    last = last.slice(0, cutAt > 0 ? cutAt : limit);
    kept[lines - 1] = last;
  }
  const visible = kept.join('\n').replace(/\s+$/, '');
  const source = text.trim().replace(/\r\n?/g, '\n');
  // On retrouve la position dans le texte d'origine pour fournir la partie masquée.
  const compact = (s: string) => s.replace(/\s+/g, '');
  const target = compact(visible).length;
  let seen = 0;
  let idx = 0;
  while (idx < source.length && seen < target) {
    if (!/\s/.test(source[idx])) seen += 1;
    idx += 1;
  }
  return { visible, hidden: source.slice(idx).trim(), truncated: true };
}

export interface Stats {
  chars: number;
  words: number;
  lines: number;
  paragraphs: number;
  hashtags: number;
  longestParagraph: number;
}

const HASHTAG = /(^|\s)#[\p{L}\p{N}_]+/gu;
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-z0-9-]+\.(?:com|fr|io|co|net|org|ai|app|dev|me|ly|eu)(?:\/[^\s]*)?\b)/giu;

export function computeStats(text: string): Stats {
  const t = text.replace(/\r\n?/g, '\n').trim();
  const paragraphs = t ? t.split(/\n\s*\n/).filter((p) => p.trim()) : [];
  return {
    chars: [...t].length,
    words: t ? t.split(/\s+/).length : 0,
    lines: t ? t.split('\n').length : 0,
    paragraphs: paragraphs.length,
    hashtags: (t.match(HASHTAG) ?? []).length,
    longestParagraph: paragraphs.reduce((m, p) => Math.max(m, [...p.trim()].length), 0),
  };
}

export type Severity = 'problem' | 'warning' | 'ok';

export interface Finding {
  id: 'length' | 'hook' | 'link' | 'wall' | 'hashtags';
  severity: Severity;
  title: string;
  detail: string;
  fix?: string;
  /** Lien interne vers le guide qui explique la règle. */
  guide?: { href: string; label: string };
}

/** Position de la première ponctuation de fin de phrase, -1 sinon. */
function firstSentenceEnd(line: string): number {
  const m = line.match(/[.!?…:](\s|$)/);
  return m && m.index !== undefined ? m.index : -1;
}

export function analyze(text: string): { stats: Stats; cuts: Record<Device, Cut>; findings: Finding[] } {
  const stats = computeStats(text);
  const cuts = { mobile: computeCut(text, 'mobile'), desktop: computeCut(text, 'desktop') };
  const findings: Finding[] = [];
  const t = text.replace(/\r\n?/g, '\n').trim();

  // 1. Longueur : seule règle officielle.
  if (stats.chars > LINKEDIN_MAX_CHARS) {
    findings.push({
      id: 'length',
      severity: 'problem',
      title: `Trop long : ${stats.chars} caractères pour 3 000 autorisés`,
      detail: `LinkedIn refuse la publication au-delà de 3 000 caractères. Il en faut retirer ${stats.chars - LINKEDIN_MAX_CHARS}.`,
      fix: 'Coupez le dernier tiers, ou publiez la version longue en article et gardez le post pour l\'idée principale.',
    });
  } else {
    findings.push({
      id: 'length',
      severity: 'ok',
      title: `Longueur valide : ${stats.chars} caractères sur 3 000`,
      detail: `Il reste ${LINKEDIN_MAX_CHARS - stats.chars} caractères avant la limite de LinkedIn.`,
    });
  }

  // 2. Accroche : la première phrase doit tenir dans ce qui est lu avant « voir plus ».
  const firstLine = t.split('\n')[0] ?? '';
  const mobile = cuts.mobile;
  const sentenceEnd = firstSentenceEnd(firstLine);
  const sentence = sentenceEnd !== -1 ? firstLine.slice(0, sentenceEnd + 1) : firstLine;
  const compactLen = (x: string) => x.replace(/\s+/g, '').length;
  const fitsMobile = !mobile.truncated || compactLen(mobile.visible) >= compactLen(sentence);
  if (!mobile.truncated) {
    findings.push({
      id: 'hook',
      severity: 'ok',
      title: 'Le post tient en entier avant « voir plus »',
      detail: "Sur mobile, tout le texte est visible sans clic : l'accroche et la suite sont lues d'un coup.",
    });
  } else if (!fitsMobile) {
    findings.push({
      id: 'hook',
      severity: sentenceEnd === -1 ? 'problem' : 'warning',
      title: 'La première phrase est coupée par « voir plus » sur mobile',
      detail: `Votre première phrase fait ${sentence.length} caractères ; environ ${mobile.visible.replace(/\s+/g, ' ').length} sont visibles sur mobile. Le lecteur voit « …${mobile.visible.replace(/\s+/g, ' ').slice(-30)} voir plus » sans avoir lu l'idée complète.`,
      fix: 'Réécrivez l\'ouverture en une phrase courte qui pose le sujet ou le résultat, puis un retour à la ligne.',
    });
  } else {
    findings.push({
      id: 'hook',
      severity: 'ok',
      title: `Accroche complète avant la coupure (${sentence.length} caractères)`,
      detail: "La première phrase se lit en entier sur mobile avant « voir plus ». Vérifiez qu'elle donne une raison de cliquer, pas seulement un début de phrase.",
    });
  }

  // 3. Lien dans le corps.
  const urls = t.match(URL_RE) ?? [];
  if (urls.length > 0) {
    const pos = t.search(URL_RE);
    const visibleMobile = mobile.truncated ? mobile.visible.replace(/\s+/g, '') : t.replace(/\s+/g, '');
    const beforeCut = mobile.truncated && t.slice(0, pos).replace(/\s+/g, '').length < visibleMobile.length;
    findings.push({
      id: 'link',
      severity: 'warning',
      title: `${urls.length} lien${urls.length > 1 ? 's' : ''} dans le corps du post`,
      detail: beforeCut
        ? 'Le lien apparaît dans la partie visible avant « voir plus » : c\'est le pire emplacement, il occupe la place de l\'accroche.'
        : 'Plusieurs études mesurent une portée moindre pour les posts qui contiennent un lien, surtout sur les pages entreprise. L\'écart varie fortement selon les études.',
      fix: 'Si le clic compte plus que la portée, placez le lien en fin de texte. Sinon, mettez-le en premier commentaire.',
      guide: { href: '/blog/mettre-un-lien-dans-un-post-linkedin?lang=fr', label: 'Mettre un lien dans un post LinkedIn : ce que ça coûte' },
    });
  } else {
    findings.push({
      id: 'link',
      severity: 'ok',
      title: 'Aucun lien dans le corps',
      detail: 'Pas de pénalité de lien à craindre sur ce post.',
    });
  }

  // 4. Mur de texte.
  if (stats.longestParagraph > 400 || (stats.chars > 600 && stats.paragraphs <= 1)) {
    findings.push({
      id: 'wall',
      severity: stats.longestParagraph > 700 ? 'problem' : 'warning',
      title: `Mur de texte : un bloc de ${stats.longestParagraph} caractères`,
      detail: `Sur mobile, un bloc de cette taille tient sur environ ${Math.ceil(stats.longestParagraph / DEVICES.mobile.width)} lignes sans respiration. Vos ${stats.paragraphs} paragraphe${stats.paragraphs > 1 ? 's' : ''} ne laissent pas l'œil se repérer.`,
      fix: 'Coupez en paragraphes de 1 à 3 lignes, séparés par une ligne vide. Repère éditorial NeverZero (à partir de 400 caractères), pas une règle LinkedIn.',
    });
  } else {
    findings.push({
      id: 'wall',
      severity: 'ok',
      title: 'Texte aéré',
      detail: stats.paragraphs > 0 ? `Le plus long bloc fait ${stats.longestParagraph} caractères.` : 'Rien à découper pour le moment.',
    });
  }

  // 5. Hashtags.
  const tagLines = t.split('\n');
  const tagsInMiddle = tagLines.some(
    (l, i) => i < tagLines.length - 1 && /(^|\s)#[\p{L}\p{N}_]+/u.test(l) && l.replace(HASHTAG, '').trim() !== '',
  );
  if (stats.hashtags > 5) {
    findings.push({
      id: 'hashtags',
      severity: 'warning',
      title: `${stats.hashtags} hashtags`,
      detail: 'LinkedIn ne fixe pas de plafond, mais au-delà de 5 le post ressemble à une liste de mots-clés. Repère éditorial NeverZero.',
      fix: 'Gardez 2 à 3 hashtags précis, placés à la fin.',
    });
  } else if (stats.hashtags > 0 && tagsInMiddle) {
    findings.push({
      id: 'hashtags',
      severity: 'warning',
      title: 'Hashtags au milieu du texte',
      detail: `${stats.hashtags} hashtag${stats.hashtags > 1 ? 's' : ''} intégré${stats.hashtags > 1 ? 's' : ''} dans les phrases : ils coupent la lecture et prennent la place de l'accroche s'ils sont dans les premières lignes.`,
      fix: 'Déplacez-les sur la dernière ligne.',
    });
  } else {
    findings.push({
      id: 'hashtags',
      severity: 'ok',
      title: stats.hashtags === 0 ? 'Pas de hashtag' : `${stats.hashtags} hashtag${stats.hashtags > 1 ? 's' : ''}, bien placé${stats.hashtags > 1 ? 's' : ''}`,
      detail: 'Rien à corriger de ce côté.',
    });
  }

  return { stats, cuts, findings };
}

export function reportText(findings: Finding[]): string {
  const label: Record<Severity, string> = { problem: 'À corriger', warning: 'À surveiller', ok: 'OK' };
  return findings
    .map((f) => `[${label[f.severity]}] ${f.title}\n${f.detail}${f.fix ? `\n→ ${f.fix}` : ''}`)
    .join('\n\n');
}
